import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
  H6,
  InputEmail,
} from "@sellar-io/components";
import { BusinessUserListResponseType } from "@sellar-io/api-v2/types";
import { ReactElement, useState } from "react";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";
import {
  Icon,
  ListElement,
  ProfilePicture,
  SpinnerLegacy,
} from "../../../../components";
import { Alert } from "../../../../components/alert/Alert";
import { ErrorMessage } from "../../../../components/errorMessage/ErrorMessage";
import { InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../components/modal/Dialog";
import { useSellarUser } from "../../../../hooks";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { useBusinessUserDelete } from "../../../../hooks/api/v2/businessUser/useBusinessUserDelete";
import { useBusinessUserInvite } from "../../../../hooks/api/v2/businessUser/useBusinessUserInvite";
import { useBusinessUserList } from "../../../../hooks/api/v2/businessUser/useBusinessUserList";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { Tooltip } from "@sellar-io/components";
import { TooltipTrigger } from "@sellar-io/components";
import { TooltipContent } from "@sellar-io/components";

export const BusinessUserSettings = (): ReactElement => {
  const businessUserListQuery = useBusinessUserList({});
  const { user } = useSellarUser();
  const [inviteDialogActive, setInviteDialogActive] = useState(false);
  const currentBusinessUser = businessUserListQuery.data?.pages[0]?.find(
    (businessUser) => businessUser.User.id === user?.id,
  );
  if (businessUserListQuery.isError)
    return (
      <ErrorMessage>
        {apiV2AssertError(businessUserListQuery.error)?.response?.data.message}
      </ErrorMessage>
    );

  if (businessUserListQuery.isPending) return <SpinnerLegacy />;

  return (
    <Card className="bg-white">
      <CardHeader className={twMerge("bg-white", "p-4")}>
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <H5>Business users</H5>
          </div>
          <Button onClick={() => setInviteDialogActive(true)}>
            <Icon icon={"PLUS"} />
            Add user
          </Button>
        </div>
      </CardHeader>
      <CardContent className={"p-4"}>
        <div className="flex flex-col gap-6">
          {(businessUserListQuery.data.pages[0]?.length === 0 ||
            !currentBusinessUser) &&
          user?.type !== "admin" ? (
            <ListElement>No users.</ListElement>
          ) : (
            businessUserListQuery.data.pages.map((page) =>
              page.map((businessUser) => (
                <BusinessUser
                  businessUser={businessUser}
                  key={businessUser.id}
                  currentBusinessUser={currentBusinessUser}
                />
              )),
            )
          )}
        </div>
      </CardContent>
      <AddBusinessUserDialog
        active={inviteDialogActive}
        onDismiss={() => setInviteDialogActive(false)}
      />
    </Card>
  );
};

const AddBusinessUserDialog = ({
  active,
  onDismiss,
}: {
  active: boolean;
  onDismiss: () => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  if (!business) throw new Error("Cannot find business");

  const businessUserInvite = useBusinessUserInvite(business.id);
  const formId = v4();
  const { register, handleSubmit, reset } = useForm<{ email: string }>({
    shouldUseNativeValidation: true,
  });

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={"Invite a user"}
      />
      <DialogContent>
        {businessUserInvite.isError && (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(businessUserInvite.error)?.response?.data
                .message ?? ""
            }
          />
        )}
        <form
          id={formId}
          onSubmit={handleSubmit((data) =>
            businessUserInvite.mutate(
              {
                email: data.email,
              },
              {
                onSuccess: () => {
                  reset();
                  onDismiss();
                },
              },
            ),
          )}
        >
          <InputContainer>
            <InputEmail
              placeholder={"Email address"}
              {...register("email", { required: true })}
            />
          </InputContainer>
        </form>
      </DialogContent>
      <DialogCTA>
        <Button
          loading={businessUserInvite.isPending}
          form={formId}
          type={"submit"}
        >
          Add user
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

const BusinessUser = ({
  businessUser,
  currentBusinessUser,
}: {
  businessUser: BusinessUserListResponseType[number];
  currentBusinessUser?: BusinessUserListResponseType[number];
}) => {
  const { user } = useSellarUser();
  const isCurrentUser = businessUser.User.id == user?.id;
  const businessUserDelete = useBusinessUserDelete(businessUser.id);
  const [modalActive, setModalActive] = useState(false);
  const isBusinessLegalEntityAdmin = businessUser.isBusinessLegalEntityAdmin;

  return (
    <div key={businessUser.id} className="flex items-center justify-between">
      <div className={"flex items-center gap-3"}>
        <ProfilePicture
          name={businessUser.User.name}
          logoUrl={businessUser.User.profileImageUrl}
        />
        <div className="flex flex-col">
          <H5>
            {businessUser.User.name ?? ""} {isCurrentUser && " (You)"}
          </H5>
          <H6 className="select-text">
            {[businessUser.jobTitle, businessUser.User.email]
              .filter(Boolean)
              .join(" - ")}
          </H6>
        </div>
      </div>

      <div className="flex items-center">
        {businessUser.role === "owner" && (
          <div className="mx-1 flex items-center md:mx-2">
            <H6 className="mx-2 hidden text-sm text-zinc-800 md:block">
              Owner
            </H6>
            <Icon icon={"SHIELD_CROWN_OUTLINE"} size={1}></Icon>
          </div>
        )}
        {!isBusinessLegalEntityAdmin && (
          <Tooltip>
            <TooltipTrigger>
              <Button
                size="small"
                color="inverse-red"
                onClick={() => setModalActive(true)}
                disabled={
                  (isCurrentUser || currentBusinessUser?.role !== "owner") &&
                  user?.type !== "admin"
                }
              >
                Remove
              </Button>
            </TooltipTrigger>
            <TooltipContent className="rounded-sm bg-zinc-800 px-2 py-1 text-xs text-white">
              Owner only
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <Dialog active={modalActive} onDismiss={() => setModalActive(false)}>
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={() => setModalActive(false)}
          title={`Delete ${businessUser.User.name}?`}
          description={"They will no longer have access to this business."}
        />
        <DialogCTA>
          <Button
            color={"red"}
            loading={businessUserDelete.isPending}
            onClick={() =>
              businessUserDelete.mutate(undefined, {
                onSuccess: () => setModalActive(false),
              })
            }
          >
            Delete
          </Button>
        </DialogCTA>
      </Dialog>
    </div>
  );
};
